import React, { useEffect, useRef, useContext, useState } from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DATE_FORMAT } from '~/Redux/Constants';
import { DAY_VIEW, MONTH_VIEW, WEEK_VIEW } from '~/Config/CalendarViews';
import moment from '~/Providers/Moment';
import { boundsForPoints } from '~/Components/Global/Drawing/DrawingUtils';
import { useResizeEffect } from '~/util';
import { DrawingContext } from './DrawingProvider';

import './DrawingDisplay.scss';

const PK_DRAWING_FORMAT = 'PKDrawingData/1';

export const ALIGN_CENTER = 'CENTER';
export const ALIGN_TOP_CENTER = 'TOP_CENTER';

const checkDrawingCanBeRendered = (drawing, views, start, end, listId) => {
  const startDate = moment(start, DATE_FORMAT);
  const endDate = moment(end, DATE_FORMAT);
  const drawingDate = moment(drawing.date, DATE_FORMAT);

  return (
    views.indexOf(drawing.calendar_view) >= 0 &&
    ((listId && drawing.user_list_id === listId) ||
      (drawing.date &&
        startDate &&
        endDate &&
        !startDate.isAfter(drawingDate) &&
        drawingDate.isBefore(endDate)))
  );
};

export const DrawingDisplay = ({ startDate, endDate, views, listId, listRef, alignment }) => {
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const { rectForDate, rerenderNumber, setRerenderNumber } = useContext(DrawingContext);
  const drawings = useSelector(({ Drawings: { drawings } }) => drawings);
  const [size, setSize] = useState({});

  const boundsForDrawing = drawing => {
    const {
      data: { format },
    } = drawing;
    if (format === PK_DRAWING_FORMAT) {
      const [left, top, width, height] = drawing.data.bounds;
      return {
        left,
        top,
        width,
        height,
        centerX: left + width * 0.5,
        centerY: top + height * 0.5,
      };
    }
    return boundsForPoints(drawing.data.points);
  };

  const positionForDrawing = drawing => {
    if (!canvasRef.current) {
      setRerenderNumber(rerenderNumber + 1);
      return { x: 0, y: 0 };
    }
    const globalRect = canvasRef.current.getBoundingClientRect();
    let rectForPositioning;

    if ([MONTH_VIEW, WEEK_VIEW, DAY_VIEW].includes(drawing.calendar_view)) {
      rectForPositioning = (() => {
        const customRect = rectForDate(moment(drawing.date, DATE_FORMAT).format(DATE_FORMAT));
        if (customRect) {
          return customRect;
        } else {
          setRerenderNumber(rerenderNumber + 1);
          return null;
        }
      })();
    } else if (drawing.user_list_id) {
      rectForPositioning = globalRect;
    } else {
      rectForPositioning = globalRect;
    }
    if (!rectForPositioning) {
      return { x: 0, y: 0 };
    }

    rectForPositioning.center = {
      x: rectForPositioning.right - rectForPositioning.width / 2,
      y: rectForPositioning.bottom - rectForPositioning.height / 2,
    };

    const drawingInfo = boundsForDrawing(drawing);

    const distanceRelativeToCenterOfBox = {
      x: drawing.position.centerX * rectForPositioning.width,
      y: drawing.position.centerY * rectForPositioning.height,
    };

    let scale = 1;

    scale =
      ((1 - drawing.position.positionRight - drawing.position.positionLeft) *
        rectForPositioning.width) /
      drawingInfo.width;

    console.log(
      'DrawingDisplay',
      'Drawing Info:',
      drawingInfo,
      'Position Right:',
      drawing.position.positionRight,
      'Position Left:',
      drawing.position.positionLeft,
      'Position Ratio:',
      1 - drawing.position.positionRight - drawing.position.positionLeft,
      'Drawing Width:',
      drawingInfo.width,
      'Canvas Width:',
      rectForPositioning.width,
      'Scale:',
      scale
    );

    const centerPosition = {
      x:
        rectForPositioning.center.x -
        globalRect.x -
        drawingInfo.width * 0.5 * scale +
        distanceRelativeToCenterOfBox.x,
      y:
        rectForPositioning.center.y -
        globalRect.y -
        drawingInfo.height * 0.5 * scale +
        distanceRelativeToCenterOfBox.y,
      scale,
    };

    if (alignment === ALIGN_TOP_CENTER && listRef) {
      const scrollTop = listRef.querySelector('.ql-editor')?.scrollTop || 0;
      console.log('DrawingDisplay', drawing.position, centerPosition);
      return {
        x: centerPosition.x,
        y: drawing.position.positionTop * rectForPositioning.height - scrollTop,
        scale,
      };
    }

    return centerPosition;
  };

  useEffect(() => {
    const onScroll = () => {
      setSize({ ...size, timestamp: moment().unix() });
    };
    if (listRef) listRef.querySelector('.ql-editor')?.addEventListener('scroll', onScroll);
    return () => {
      if (listRef) listRef.querySelector('.ql-editor')?.removeEventListener('scroll', onScroll);
    };
  }, [listRef, size, setSize]);

  useEffect(() => {
    const canvas = canvasRef.current;

    const { width, height } = canvas.parentElement.parentElement.getBoundingClientRect();

    canvas.width = width;
    canvas.height = height;

    const context = canvasRef.current.getContext('2d');
    contextRef.current = context;
    contextRef.current.lineCap = 'round';
    contextRef.current.lineJoin = 'round';
  }, [startDate, endDate, listId, views, canvasRef, contextRef]);

  useEffect(() => {
    const renderDrawing = drawing => {
      const position = positionForDrawing(drawing);

      if (!position) {
        return;
      }

      const drawingPoints = drawing.data.points;

      contextRef.current.beginPath();
      contextRef.current.globalAlpha = 1;
      contextRef.current.moveTo(
        drawingPoints[0].dx * position.scale + position.x,
        drawingPoints[0].dy * position.scale + position.y
      );

      for (let i = 1; i < drawingPoints.length; i++) {
        if (drawingPoints[i]) {
          contextRef.current.strokeStyle = drawingPoints[i].color;
          contextRef.current.lineWidth = drawingPoints[i].strokeWidth * position.scale;
          contextRef.current.lineTo(
            drawingPoints[i].dx * position.scale + position.x,
            drawingPoints[i].dy * position.scale + position.y
          );
          contextRef.current.stroke();
        } else {
          contextRef.current.closePath();
          contextRef.current.beginPath();
          if (drawingPoints[i + 1]) {
            contextRef.current.moveTo(
              drawingPoints[i + 1].dx * position.scale + position.x,
              drawingPoints[i + 1].dy * position.scale + position.y
            );
          }
        }
      }
    };

    contextRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    const drawingsToRender = drawings.filter(drawing =>
      checkDrawingCanBeRendered(drawing, views, startDate, endDate, listId)
    );

    // render by points
    drawingsToRender
      .filter(({ data: { format } }) => format !== PK_DRAWING_FORMAT)
      .forEach(drawing => renderDrawing(drawing));
  }, [drawings, canvasRef, startDate, endDate, listId, views]);

  useResizeEffect(() => {
    setSize({ width: canvasRef.current.width, height: canvasRef.current.height });
  });

  return (
    <div className="drawing-display-wrapper">
      <canvas ref={canvasRef} className="display" />
      {drawings
        .filter(drawing => checkDrawingCanBeRendered(drawing, views, startDate, endDate, listId))
        .filter(({ data: { format } }) => format === PK_DRAWING_FORMAT)
        .map(drawing => {
          const { x, y, scale } = positionForDrawing(drawing);
          const { width, height } = boundsForDrawing(drawing);
          return (
            <img
              className="drawing"
              key={drawing.id}
              src={`data:image/png;base64,${drawing.data.png}`}
              style={{ left: x, top: y, width: width * scale, height: height * scale }}
            />
          );
        })}
    </div>
  );
};

DrawingDisplay.propTypes = {
  startDate: propTypes.string,
  endDate: propTypes.string,
  listId: propTypes.number,
  views: propTypes.arrayOf(propTypes.string).isRequired,
  alignment: propTypes.oneOf([ALIGN_CENTER, ALIGN_TOP_CENTER]).isRequired,
  listRef: propTypes.shape({ current: propTypes.instanceOf(Element) }),
};

DrawingDisplay.defaultProps = {
  alignment: ALIGN_CENTER,
};
